import React, { useEffect, useState } from "react";
import { Loader2, SaveIcon, SquarePen, X } from "lucide-react";
import ButtonV2 from "../../button/v2";
import SelectDropdownV2 from "../../selectdropdown/v2";
import DateCell from "../../baseDataTable/cells/DateCell";
import {
  formatIndianCompactNumber,
  formatIndianInputNumeric,
  normalizeNumericWithDecimal,
} from "../../../../lib/utils";


export const IDENTIFIER_RULES = {
  /** AAA-ZZZ | entity type | surname initial | 0001-9999 | check letter */
  pan: {
    regex: /^[A-Z]{3}[ABCFGHLJPT][A-Z][0-9]{4}[A-Z]$/,
    length: 10,
    placeholder: "Enter 10-character PAN",
    error: "Enter a valid PAN, e.g. ABCPD1234E",
  },
  /** 12 digits, never starting with 0 or 1 */
  aadhaar: {
    regex: /^[2-9][0-9]{11}$/,
    length: 12,
    placeholder: "Enter 12-digit Aadhaar",
    error: "Enter a valid 12-digit Aadhaar number",
  },
  /** state code | the holder's PAN | entity number | 'Z' | checksum */
  gstin: {
    regex: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
    length: 15,
    placeholder: "Enter 15-character GSTIN",
    error: "Enter a valid GSTIN, e.g. 27ABCPD1234E1Z5",
  },
  /** listing status | industry code | state | year | company class | reg. number */
  cin: {
    regex: /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
    length: 21,
    placeholder: "Enter 21-character CIN",
    error: "Enter a valid CIN, e.g. U72200KA2010PTC123456",
  },
  /** LOU prefix | reserved "00" | entity part | 2 check digits */
  lei: {
    regex: /^[0-9A-Z]{4}00[0-9A-Z]{12}[0-9]{2}$/,
    length: 20,
    placeholder: "Enter 20-character LEI",
    error: "Enter a valid 20-character LEI",
  },
  din: {
    regex: /^[0-9]{8}$/,
    length: 8,
    placeholder: "Enter 8-digit DIN",
    error: "Enter a valid 8-digit DIN",
  },
  /** 1 letter | 7 digits */
  passport: {
    regex: /^[A-Z][0-9]{7}$/,
    length: 8,
    placeholder: "Enter 8-character Passport number",
    error: "Enter a valid Passport number, e.g. M1234567",
  },
  /** UDYAM | state code | district code | 7-digit serial */
  udyam: {
    regex: /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/,
    length: 19,
    placeholder: "Enter Udyam registration number",
    error: "Enter a valid Udyam number, e.g. UDYAM-KR-03-0000001",
  },
} as const;

export type IdentifierType = keyof typeof IDENTIFIER_RULES;

export const isIdentifierType = (type: string): type is IdentifierType =>
  type in IDENTIFIER_RULES;

export const sanitizeIdentifierInput = (
  type: IdentifierType,
  raw: string,
): string => {
  const { length } = IDENTIFIER_RULES[type];
  if (type === "din") return raw.replace(/\D/g, "").slice(0, length);
  const allowed =
    type === "aadhaar"
      ? /[^0-9X]/g
      : type === "udyam"
        ? /[^A-Z0-9-]/g
        : /[^A-Z0-9]/g;
  return raw.toUpperCase().replace(allowed, "").slice(0, length);
};

export const validateIdentifier = (
  type: IdentifierType,
  value?: string | null,
): string | null => {
  const trimmed = String(value ?? "")
    .trim()
    .replace(/\s/g, "");
  if (trimmed === "") return null; 
  const { regex, error } = IDENTIFIER_RULES[type];
  return regex.test(trimmed) ? null : error;
};

export type EditableFieldType =
  | "text"
  | "float"
  | "integer"
  | "currency"
  | "date"
  | "textarea"
  | "select"
  | IdentifierType;

export interface OptionNameValuePair {
  name: string;
  value: string;
}

export interface EditableFieldProps {
  label: string;
  value: any;
  type: EditableFieldType;
  isEditable?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  isLoading?: boolean;
  onSave?: (value: any) => Promise<boolean>;
  options?: OptionNameValuePair[];
  placeholder?: string;
  displayValue?: React.ReactNode;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditCancel?: () => void;
}

export const maskAadhaarNumber = (val: string): string => {
  if (!val) return "";
  const digits = String(val).replace(/\D/g, "");
  return digits.length > 3 ? `XXXX XXXX ${digits.slice(-4)}` : val;
};

export const maskAadhaarForStorage = (val: string): string =>
  `XXXXXXXX${String(val).replace(/\D/g, "").slice(-4)}`;

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  type,
  isEditable = true,
  disabled = false,
  disabledMessage,
  isLoading = false,
  onSave,
  options = [],
  placeholder,
  displayValue,
  isEditing = false,
  onEditStart,
  onEditCancel,
}) => {
  const formatInitialEditValue = (val: any) => {
    if (val === null || val === undefined || val === "") return "";
    const valStr = String(val);
    if (type === "currency") {
      const raw = normalizeNumericWithDecimal(valStr);
      return formatIndianInputNumeric(raw);
    }
    if (type === "aadhaar") {
      const digits = valStr.replace(/\D/g, "");
      return digits.length === 4 ? `XXXXXXXX${digits}` : valStr;
    }
    return valStr;
  };

  const [editValue, setEditValue] = useState<string>(() =>
    formatInitialEditValue(value)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const inputPlaceholder = placeholder || `Enter ${label.toLowerCase()}`;

  useEffect(() => {
    if (!isEditing) {
      setEditValue(formatInitialEditValue(value));
      setInputError(null);
    }
  }, [value, isEditing, type]);

  const handleEdit = () => {
    if (!isEditable || !onSave || disabled || isSaving || isLoading) return;
    setEditValue(formatInitialEditValue(value));
    setInputError(null);
    onEditStart?.();
  };

  const handleCancel = () => {
    setEditValue(formatInitialEditValue(value));
    setInputError(null);
    onEditCancel?.();
  };

  const validateInput = (): string | null => {
    if (!isIdentifierType(type)) return null;

    const trimmed = editValue.trim();

    if (type === "aadhaar" && trimmed.includes("X")) {
      return trimmed === formatInitialEditValue(value).trim()
        ? null
        : "Enter the full 12-digit Aadhaar number to update it";
    }

    return validateIdentifier(type, trimmed);
  };

  const handleSave = async () => {
    if (!onSave || disabled || isLoading) return;

    const errorMsg = validateInput();
    if (errorMsg) {
      setInputError(errorMsg);
      return;
    }
    setInputError(null);

    let parsedValue: any = editValue.trim();

    if (type === "currency") {
      const rawNumeric = normalizeNumericWithDecimal(editValue);
      parsedValue = rawNumeric === "" ? null : Number(rawNumeric);
      if (parsedValue !== null && Number.isNaN(parsedValue)) return;
    } else if (type === "float" || type === "integer") {
      if (editValue === "") {
        parsedValue = null;
      } else {
        const num = Number(editValue);
        if (Number.isNaN(num)) return;
        parsedValue = type === "integer" ? Math.floor(num) : num;
      }
    } else if (type === "aadhaar") {
      parsedValue =
        editValue.trim() === "" ? null : maskAadhaarForStorage(editValue);
    } else if (isIdentifierType(type)) {
      parsedValue = editValue.trim() === "" ? null : editValue.trim().toUpperCase();
    }

    setIsSaving(true);
    try {
      await onSave(parsedValue);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      type === "integer" &&
      (event.key === "." ||
        event.key === "," ||
        event.key === "e" ||
        event.key === "E")
    ) {
      event.preventDefault();
    }

    if (type === "aadhaar" || type === "din") {
      if (event.key.length === 1 && !/^[0-9]$/.test(event.key) && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
      }
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInputError(null);
    const rawVal = event.target.value;

    if (type === "currency") {
      const normalized = normalizeNumericWithDecimal(rawVal);
      const formatted = formatIndianInputNumeric(normalized);
      setEditValue(formatted);
    } else if (isIdentifierType(type)) {
      setEditValue(sanitizeIdentifierInput(type, rawVal));
    } else {
      setEditValue(rawVal);
    }
  };

  const renderedValue =
    displayValue !== undefined
      ? displayValue
      : value !== null && value !== undefined && value !== ""
        ? type === "currency" && !Number.isNaN(Number(value))
          ? formatIndianCompactNumber(Number(value), { includeCurrency: false })
          : type === "aadhaar"
            ? maskAadhaarNumber(String(value))
            : String(value)
        : placeholder ?? "--";

  const selectedOption =
    editValue !== ""
      ? options.find((option) => option.value === editValue)
      : undefined;

  const renderActionButtons = () => {
    if (isSaving) {
      return (
        <span className="shrink-0 text-xs font-medium italic text-[#71717A]">
          Saving...
        </span>
      );
    }

    return (
      <div className="flex shrink-0 items-center gap-1">
        <ButtonV2
          variant="ghost"
          className="h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent"
          onClick={() => void handleSave()}
          disabled={isSaving}
          prefix={
            <SaveIcon
              className="text-[#71717A] hover:text-[#0A0A0A]"
              width={14}
              height={14}
            />
          }
        />
        <ButtonV2
          variant="ghost"
          className="h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent"
          onClick={handleCancel}
          disabled={isSaving}
          prefix={
            <X
              className="text-[#71717A] hover:text-[#0A0A0A]"
              width={14}
              height={14}
            />
          }
        />
      </div>
    );
  };

  return (
    <div
      className={`flex w-full gap-4 ${
        type === "textarea" ? "items-start" : "items-center"
      }`}
    >
      <div className="w-[180px] shrink-0">
        <span className="text-xs font-medium leading-4 text-[#71717A]">
          {label}
        </span>
      </div>

      <div
        className={`flex min-w-0 flex-1 ${
          type === "textarea" ? "items-start" : "items-center"
        }`}
      >
        {isEditing ? (
          <div className="flex w-full flex-col">
            <div
              className={`flex min-w-0 flex-1 justify-between gap-2 rounded-md border border-[#818CF8] bg-white px-2 py-1 shadow-sm focus-within:ring-1 focus-within:ring-[#818CF8] ${
                type === "textarea" ? "items-start" : "items-center"
              }`}
            >
              {type === "select" ? (
                <div className="min-w-0 flex-1">
                  <SelectDropdownV2
                    selectedOption={selectedOption}
                    name={label}
                    placeholder="Search"
                    options={options}
                    onSelectionChange={(option) => {
                      setInputError(null);
                      setEditValue(option.value);
                    }}
                    disabled={isSaving}
                    enableSearch
                    containerClassName="w-full [&_span]:!text-xs [&_span]:!font-medium [&_span]:!leading-4"
                    selectedContainerClassName="w-full !p-0"
                    titleClassName="!text-xs !font-medium !leading-4 !text-[#0A0A0A]"
                    dropdownClassName="!w-[240px] !max-w-[240px]"
                    optionClassName="!text-xs !font-medium !leading-4 !text-[#0A0A0A] !px-2 !py-1"
                    showOptionTooltip
                    {...{
                      className: "[&>div>svg]:!h-3 [&>div>svg]:!w-3",
                    }}
                  />
                </div>
              ) : type === "textarea" ? (
                <textarea
                  autoFocus
                  rows={4}
                  placeholder={inputPlaceholder}
                  value={editValue}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  className="w-full min-w-0 flex-1 resize-none border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
                />
              ) : type === "date" ? (
                <div className="flex min-w-0 flex-1 items-center">
                  <DateCell
                    value={editValue}
                    isEditable={true}
                    onChange={(newValue) => {
                      setInputError(null);
                      setEditValue(newValue);
                    }}
                    className="w-full !p-0"
                    inputClassName="!h-4 !w-full !min-w-0 !flex-1 !border-none !bg-transparent !p-0 !text-xs !font-medium !leading-4 !text-[#0A0A0A] !outline-none focus:!ring-0 [&::-webkit-datetime-edit-fields-wrapper]:!p-0 [&_*]:focus:!bg-transparent [&_*]:focus:!text-[#0A0A0A]"
                  />
                </div>
              ) : isIdentifierType(type) ? (
                <input
                  autoFocus
                  type="text"
                  maxLength={IDENTIFIER_RULES[type].length}
                  value={editValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isSaving}
                  placeholder={IDENTIFIER_RULES[type].placeholder}
                  className="h-4 w-full min-w-0 flex-1 border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
                />
              ) : (
                <input
                  autoFocus
                  type={
                    type === "float" || type === "integer" ? "number" : "text"
                  }
                  step={
                    type === "integer"
                      ? "1"
                      : type === "float"
                        ? "any"
                        : undefined
                  }
                  value={editValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isSaving}
                  placeholder={inputPlaceholder}
                  className="h-4 w-full min-w-0 flex-1 border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
                />
              )}

              {renderActionButtons()}
            </div>
            {inputError && (
              <p className="mt-1 text-xs font-normal leading-4 text-red-600">
                {inputError}
              </p>
            )}
          </div>
        ) : (
          <div
            title={disabled ? disabledMessage : undefined}
            className={`group/val flex min-w-0 flex-1 justify-between gap-2 rounded-md border border-transparent px-2 py-1 transition-colors hover:bg-[#F3F4F6] ${
              (disabled && disabledMessage) || isLoading ? "cursor-not-allowed" : ""
            } ${
              type === "textarea" ? "items-start" : "items-center"
            }`}
          >
            {isLoading ? (
              <div className="flex h-4 items-center">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#71717A]" />
              </div>
            ) : (
              <span
                className={`min-w-0 flex-1 text-xs font-medium leading-4 text-[#0A0A0A] ${
                  type === "textarea" ? "whitespace-pre-wrap break-words" : "truncate"
                }`}
              >
                {renderedValue}
              </span>
            )}

            <div
              className={`flex h-4 w-4 shrink-0 items-center justify-end opacity-0 group-hover/val:opacity-100 ${
                disabled || isLoading ? "cursor-not-allowed" : ""
              }`}
            >
              {isEditable && onSave && !isLoading && (
                <ButtonV2
                  variant="ghost"
                  className={`h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent ${
                    disabled ? "cursor-not-allowed" : "hover:text-[#0A0A0A]"
                  }`}
                  onClick={handleEdit}
                  disabled={disabled || isSaving}
                  prefix={
                    <SquarePen
                      className={`text-[#71717A] ${
                        disabled ? "opacity-80" : ""
                      }`}
                      width={14}
                      height={14}
                    />
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableField;